// Utility functions for company management and region detection
import * as turf from "@turf/turf";

export interface Company {
  id: string;
  name: string;
  address: string; // Company address for geocoding
  coordinates: [number, number]; // [longitude, latitude] - auto-generated from address
  region?: string; // Region name where company is located - auto-detected
  regionId?: string; // Region ID - auto-detected
  topics?: string;
  membership?: string;
  displayed?: string;
}

export interface Region {
  id: string;
  name: string;
  coordinates: any; // GeoJSON coordinates
  bounds?: {
    north: number;
    south: number;
    east: number;
    west: number;
  };
}

// Hàm geocode địa chỉ (dùng Nominatim free)
export const geocodeAddress = async (
  address: string
): Promise<{ lat: number; lon: number; address: string } | null> => {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
        address
      )}&countrycodes=de&limit=1`
    );
    const data = await res.json();

    if (data.length === 0) {
      console.warn(`No geocoding results for address: ${address}`);
      return null;
    }

    const lat = parseFloat(data[0].lat);
    const lon = parseFloat(data[0].lon);
    const displayName = data[0].display_name || address;

    return { lat, lon, address: displayName };
  } catch (error) {
    console.error(`Geocoding error for address ${address}:`, error);
    return null;
  }
};

// Hàm xác định vùng dựa trên tọa độ
export const findRegionByCoordinates = (
  coordinates: [number, number],
  geoData: any
): { region: string; regionId: string } | null => {
  console.log("🔄 Finding region by coordinates:", coordinates);
  console.log("GeoData features count:", geoData?.features?.length || 0);

  if (!geoData || !geoData.features) {
    console.log("❌ No geoData or features available");
    return null;
  }

  const [longitude, latitude] = coordinates;
  console.log("Searching for point:", { longitude, latitude });

  for (let i = 0; i < geoData.features.length; i++) {
    const feature = geoData.features[i];
    console.log(`Checking feature ${i + 1}:`, {
      id: feature.id,
      objectId: feature.properties?.OBJECTID,
      kmr: feature.properties?.KMR,
      geometryType: feature.geometry?.type,
    });

    if (feature.geometry && feature.geometry.type) {
      let isInside = false;

      if (feature.geometry.type === "Polygon") {
        isInside = isPointInPolygon(coordinates, feature.geometry.coordinates);
      } else if (feature.geometry.type === "MultiPolygon") {
        isInside = feature.geometry.coordinates.some((polygon: number[][][]) =>
          isPointInPolygon(coordinates, polygon)
        );
      }

      console.log(`Feature ${i + 1} - isInside:`, isInside);

      if (isInside) {
        // Sử dụng id của feature (số) hoặc OBJECTID làm regionId
        const regionId = String(
          feature.id || feature.properties?.OBJECTID || "unknown"
        );
        // Sử dụng KMR làm tên vùng chính
        const regionName =
          feature.properties?.KMR ||
          feature.properties?.name ||
          feature.properties?.lan_name ||
          "Unknown Region";

        console.log("✅ Found region:", {
          regionName,
          regionId,
          featureId: feature.id,
        });
        return {
          region: regionName,
          regionId: regionId,
        };
      }
    }
  }

  console.log("❌ No region found for coordinates:", coordinates);
  return null;
};

// Check if a point is inside a polygon using ray casting algorithm
export const isPointInPolygon = (
  point: [number, number],
  polygon: number[][][]
): boolean => {
  const [x, y] = point;
  let inside = false;

  // For GeoJSON Polygon: polygon is an array of rings
  // polygon[0] is the exterior ring, polygon[1+] are holes
  for (const ring of polygon) {
    if (!ring || ring.length < 3) continue; // Skip invalid rings

    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const [xi, yi] = ring[i];
      const [xj, yj] = ring[j];

      // Ray casting algorithm
      if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
        inside = !inside;
      }
    }
  }

  return inside;
};

// Assign regions to companies based on their coordinates
export const assignRegionsToCompanies = (
  companies: Company[],
  geoData: any
): Company[] => {
  return companies.map((company) => {
    const regionInfo = findRegionByCoordinates(company.coordinates, geoData);

    if (regionInfo) {
      return {
        ...company,
        region: regionInfo.region,
        regionId: regionInfo.regionId,
      };
    }

    return company;
  });
};

// Geocode address and find region in one step
export const geocodeAndFindRegion = async (
  address: string,
  geoData: any
): Promise<{
  lat: number;
  lon: number;
  address: string;
  region?: string;
  regionId?: string;
} | null> => {
  // Step 1: Geocode the address
  const geocodingResult = await geocodeAddress(address);

  if (!geocodingResult) {
    return null;
  }

  // Step 2: Find which region contains these coordinates
  const regionInfo = findRegionByCoordinates(
    [geocodingResult.lon, geocodingResult.lat],
    geoData
  );

  if (regionInfo) {
    return {
      lat: geocodingResult.lat,
      lon: geocodingResult.lon,
      address: geocodingResult.address || address,
      region: regionInfo.region,
      regionId: regionInfo.regionId,
    };
  }

  return {
    lat: geocodingResult.lat,
    lon: geocodingResult.lon,
    address: geocodingResult.address || address,
  }; // Return coordinates even if region not found
};

// Interface for imported company data
export interface ImportedCompanyData {
  name: string;
  address: string;
  topics?: string;
  membership?: string;
  displayed?: string;
}

// Process imported companies with geocoding and region finding
export const processImportedCompanies = async (
  importedData: ImportedCompanyData[],
  geoData: any
): Promise<{ companies: Company[]; failedCompanies: ImportedCompanyData[] }> => {
  console.log(`🔄 Processing ${importedData.length} imported companies...`);
  console.log("GeoData structure:", geoData);

  const companies: Company[] = [];
  const failedCompanies: ImportedCompanyData[] = [];

  for (let i = 0; i < importedData.length; i++) {
    const data = importedData[i];
    console.log(
      `Processing company ${i + 1}/${importedData.length}: ${data.name}`
    );

    try {
      // Geocode the address
      const coordinates = await geocodeAddress(data.address);

      if (coordinates) {
        // Find region using the full geoData object
        const region = findRegionByCoordinates(
          [coordinates.lon, coordinates.lat],
          geoData
        );

        const company: Company = {
          id: `imported-${i + 1}`,
          name: data.name,
          address: data.address,
          coordinates: [coordinates.lon, coordinates.lat],
          region: region?.region || "Unknown Region",
          regionId: region?.regionId || "unknown",
          topics: data.topics,
          membership: data.membership,
          displayed: data.displayed,
        };

        companies.push(company);
        console.log(
          `✅ Company ${i + 1}: ${data.name} -> ${coordinates.lat}, ${
            coordinates.lon
          } (${region?.region || "Unknown"})`
        );
      } else {
        console.log(`❌ Company ${i + 1}: ${data.name} - Geocoding failed`);
        failedCompanies.push(data);
      }
    } catch (error) {
      console.error(
        `❌ Error processing company ${i + 1}: ${data.name}`,
        error
      );
      failedCompanies.push(data);
    }

    // Add small delay to avoid rate limiting
    if (i < importedData.length - 1) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
  }

  console.log(`✅ Processed ${companies.length} companies successfully`);
  console.log(`❌ Failed to geocode ${failedCompanies.length} companies`);
  return { companies, failedCompanies };
};
